import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileText, CreditCard, Settings, TestTube, Calendar } from 'lucide-react'

/**
 * Demo Pages Index
 * Central hub for all demo/testing pages
 * Route: /demo
 */
export default function DemoIndex() {
  const demoPages = [
    {
      title: 'Payment Integration Modal',
      description: 'Test the PaymentIntegrationModal with configurable branch counts to verify scrolling behaviour and layout',
      path: '/modal-demo',
      icon: CreditCard,
      status: 'active'
    },
    {
      title: 'Splash Screen',
      description: 'Preview the application splash screen animation',
      path: '/splash-demo',
      icon: Settings,
      status: 'active'
    },
    {
      title: 'Timeline Dividers',
      description: 'Compare date divider styles: pill, curved ribbon, notched bookmark, and more',
      path: '/demo/timeline-dividers',
      icon: Calendar,
      status: 'active'
    },
    // Add more demo pages here as they're created
    {
      title: 'Invoice Preview',
      description: 'Test invoice generation and PDF rendering (coming soon)',
      path: '/demo/invoice',
      icon: FileText,
      status: 'planned'
    },
    {
      title: 'Component Library',
      description: 'Visual catalogue of all UI components (coming soon)',
      path: '/demo/components',
      icon: TestTube,
      status: 'planned'
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Demo Pages</h1>
          <p className="text-muted-foreground">
            Testing and preview pages for Odin components and features
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {demoPages.map((demo) => {
            const Icon = demo.icon
            const isActive = demo.status === 'active'

            return (
              <Card
                key={demo.path}
                className={
                  isActive
                    ? 'hover:border-[hsl(var(--odin-green))] transition-colors cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                }
              >
                {isActive ? (
                  <Link to={demo.path}>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Icon className="h-6 w-6 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg">{demo.title}</CardTitle>
                          <CardDescription>{demo.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Link>
                ) : (
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Icon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {demo.title}
                          <span className="text-xs font-normal text-muted-foreground">
                            (Coming Soon)
                          </span>
                        </CardTitle>
                        <CardDescription>{demo.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                )}
              </Card>
            )
          })}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-medium text-blue-900 mb-2">Adding New Demo Pages</h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Create your demo page component in <code className="bg-blue-100 px-1 rounded">/src/pages/demo/</code></li>
            <li>Add the route in <code className="bg-blue-100 px-1 rounded">App.jsx</code></li>
            <li>Add an entry to the <code className="bg-blue-100 px-1 rounded">demoPages</code> array in this file</li>
            <li>Set status to 'active' when ready to use</li>
          </ol>
        </div>
      </div>
    </div>
  )
}
